import React, { useCallback, useEffect, useMemo, useState } from "react";
import CompanyProfile from "./CompanyProfile";
import { prettifyStatusLabel } from "./prettifyStatusLabel";
import { apiFetch, getApiBase, parseJsonOrExplain } from "./apiBase";

const icDecisionColor = {
  advance_to_ic: "#0a7f5a",
  hold_for_more_work: "#b54708",
  reject: "#b42318",
  archive: "#6b6258",
};

function formatSek(value) {
  if (value === null || value === undefined || value === "") return "–";
  const num = Number(value);
  if (Number.isNaN(num)) return "–";
  return num.toLocaleString("sv-SE") + " MSEK";
}

/** company_ic_cases.ic_confidence är 0–1 i schemat; annars visas värdet rakt av. */
function formatIcConfidence(value) {
  if (value === null || value === undefined || value === "") return "–";
  const n = Number(value);
  if (Number.isNaN(n)) return "–";
  if (n >= 0 && n <= 1) return `${Math.round(n * 100)} %`;
  return n.toLocaleString("sv-SE", { maximumFractionDigits: 2 });
}

function Section({ title, children, right }) {
  return (
    <section
      style={{
        background: "#fbfaf7",
        border: "1px solid rgba(17,17,17,0.10)",
        borderRadius: "22px",
        padding: "24px",
        marginBottom: "18px",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "end",
          gap: "12px",
          marginBottom: "16px",
        }}
      >
        <h2
          style={{
            margin: 0,
            fontSize: "28px",
            lineHeight: 1.1,
            fontWeight: 500,
            letterSpacing: "-0.02em",
          }}
        >
          {title}
        </h2>
        {right}
      </div>
      {children}
    </section>
  );
}

function formatDate(value) {
  if (!value) return "–";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString("sv-SE");
}

const SORT_KEYS = [
  { id: "last_ic_at", label: "Senaste IC" },
  { id: "company_name", label: "Bolag" },
  { id: "net_sales", label: "Omsättning" },
  { id: "ebitda", label: "EBITDA" },
  { id: "ic_confidence", label: "IC-konfidens" },
  { id: "latest_ic_decision", label: "Senaste beslut" },
];

function CompanyIcDashboard({ user }) {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(100);
  const [sortBy, setSortBy] = useState("last_ic_at");
  const [sortDir, setSortDir] = useState("desc");
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [icDecision, setIcDecision] = useState("");
  const [netSalesMinInput, setNetSalesMinInput] = useState("");
  const [netSalesMaxInput, setNetSalesMaxInput] = useState("");
  const [ebitdaMinInput, setEbitdaMinInput] = useState("");
  const [ebitdaMaxInput, setEbitdaMaxInput] = useState("");
  const [appliedNetSalesMin, setAppliedNetSalesMin] = useState("");
  const [appliedNetSalesMax, setAppliedNetSalesMax] = useState("");
  const [appliedEbitdaMin, setAppliedEbitdaMin] = useState("");
  const [appliedEbitdaMax, setAppliedEbitdaMax] = useState("");
  const [triggerFingerprint, setTriggerFingerprint] = useState("");
  const [triggerOptions, setTriggerOptions] = useState([]);
  const [showManuallyExcluded, setShowManuallyExcluded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCompanyId, setSelectedCompanyId] = useState(null);
  const [icPipelineBusy, setIcPipelineBusy] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    const params = new URLSearchParams({
      page: String(page),
      page_size: String(pageSize),
      sort_by: sortBy,
      sort_dir: sortDir,
    });
    if (search.trim()) params.set("search", search.trim());
    if (icDecision) params.set("ic_decision", icDecision);
    if (appliedNetSalesMin.trim()) {
      params.set("net_sales_min", appliedNetSalesMin.trim().replace(",", "."));
    }
    if (appliedNetSalesMax.trim()) {
      params.set("net_sales_max", appliedNetSalesMax.trim().replace(",", "."));
    }
    if (appliedEbitdaMin.trim()) {
      params.set("ebitda_min", appliedEbitdaMin.trim().replace(",", "."));
    }
    if (appliedEbitdaMax.trim()) {
      params.set("ebitda_max", appliedEbitdaMax.trim().replace(",", "."));
    }
    if (triggerFingerprint.trim()) {
      params.set("trigger_fingerprint", triggerFingerprint.trim());
    }
    if (showManuallyExcluded) {
      params.set("include_manually_excluded", "true");
    }

    apiFetch(`${getApiBase()}/directory/companies_ic?${params}`)
      .then(async (r) => parseJsonOrExplain(r))
      .then((data) => {
        setRows(data.rows || []);
        setTotal(Number(data.total || 0));
        setLoading(false);
      })
      .catch((e) => {
        setError(e.message || "Fel");
        setLoading(false);
      });
  }, [
    page,
    pageSize,
    sortBy,
    sortDir,
    search,
    icDecision,
    appliedNetSalesMin,
    appliedNetSalesMax,
    appliedEbitdaMin,
    appliedEbitdaMax,
    triggerFingerprint,
    showManuallyExcluded,
  ]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    apiFetch(`${getApiBase()}/directory/ic_triggers`)
      .then(async (r) => parseJsonOrExplain(r))
      .then((data) => setTriggerOptions(Array.isArray(data) ? data : []))
      .catch(() => setTriggerOptions([]));
  }, []);

  const triggerSelectOptions = useMemo(() => {
    return (triggerOptions || []).map((t) => ({
      fingerprint: t.fingerprint,
      label:
        (t.title && String(t.title).trim()) ||
        `${(t.fingerprint || "").slice(0, 14)}…`,
    }));
  }, [triggerOptions]);

  const onHeaderClick = (key) => {
    if (sortBy === key) {
      setSortDir((d) => (d === "desc" ? "asc" : "desc"));
    } else {
      setSortBy(key);
      setSortDir(key === "company_name" ? "asc" : "desc");
    }
    setPage(1);
  };

  const applyFilters = () => {
    setSearch(searchInput);
    setAppliedNetSalesMin(netSalesMinInput);
    setAppliedNetSalesMax(netSalesMaxInput);
    setAppliedEbitdaMin(ebitdaMinInput);
    setAppliedEbitdaMax(ebitdaMaxInput);
    setPage(1);
  };

  const runIcCompletionOnly = () => {
    if (user?.role !== "admin") return;
    setIcPipelineBusy(true);
    apiFetch(`${getApiBase()}/admin/pipeline/run-ic-only`, { method: "POST" })
      .then((r) => parseJsonOrExplain(r))
      .then(() => {
        setError(null);
      })
      .catch((e) => setError(e.message || "Kunde inte starta IC-slutföring"))
      .finally(() => setIcPipelineBusy(false));
  };

  const inputNumStyle = {
    width: "100%",
    padding: "10px 12px",
    borderRadius: "12px",
    border: "1px solid rgba(17,17,17,0.14)",
    fontSize: "14px",
    boxSizing: "border-box",
  };

  const filterLabelStyle = {
    display: "block",
    fontSize: "11px",
    textTransform: "uppercase",
    letterSpacing: "0.1em",
    color: "#5f5a52",
    marginBottom: "6px",
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <>
      <Section
        title="Bolag med IC-analys"
        right={
          <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
            <span
              style={{
                fontSize: "13px",
                color: "#5f5a52",
                fontFamily: "Arial, Helvetica, sans-serif",
              }}
            >
              {loading ? "Laddar…" : `${total} bolag`}
            </span>
            {user?.role === "admin" && (
              <button
                type="button"
                onClick={runIcCompletionOnly}
                disabled={icPipelineBusy}
                style={{
                  padding: "8px 12px",
                  borderRadius: "999px",
                  border: "1px solid rgba(17,17,17,0.2)",
                  background: "#fff",
                  cursor: icPipelineBusy ? "wait" : "pointer",
                  fontFamily: "Arial, Helvetica, sans-serif",
                  fontSize: "12px",
                }}
              >
                {icPipelineBusy ? "Startar..." : "Kör klart bolag (IC)"}
              </button>
            )}
          </div>
        }
      >
        <p
          style={{
            margin: "0 0 18px 0",
            fontSize: "15px",
            lineHeight: 1.55,
            color: "#3f3a34",
            fontFamily: 'Arial, Helvetica, sans-serif',
            maxWidth: "720px",
          }}
        >
          Alla bolag som minst en gång körts genom IC (databasen{" "}
          <span style={{ fontFamily: "ui-monospace, monospace" }}>company_ic_cases</span>).
          Senaste beslut och status per bolag. Klicka på en rad för företagsprofil.
        </p>

        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "12px",
            alignItems: "end",
            marginBottom: "14px",
            fontFamily: 'Arial, Helvetica, sans-serif',
          }}
        >
          <div style={{ flex: "1 1 220px", minWidth: "180px" }}>
            <label style={filterLabelStyle}>Sök namn / ID</label>
            <input
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && applyFilters()}
              placeholder="Bolagsnamn eller organisationsnummer"
              style={inputNumStyle}
            />
          </div>
          <div style={{ flex: "0 1 200px" }}>
            <label style={filterLabelStyle}>Senaste beslut</label>
            <select
              value={icDecision}
              onChange={(e) => {
                setIcDecision(e.target.value);
                setPage(1);
              }}
              style={{
                width: "100%",
                padding: "10px 12px",
                borderRadius: "12px",
                border: "1px solid rgba(17,17,17,0.14)",
                fontSize: "14px",
                background: "#fff",
              }}
            >
              <option value="">Alla</option>
              <option value="advance_to_ic">
                {prettifyStatusLabel("advance_to_ic")}
              </option>
              <option value="hold_for_more_work">
                {prettifyStatusLabel("hold_for_more_work")}
              </option>
              <option value="reject">{prettifyStatusLabel("reject")}</option>
              <option value="archive">{prettifyStatusLabel("archive")}</option>
            </select>
          </div>
          <div style={{ flex: "2 1 320px", minWidth: "240px" }}>
            <label style={filterLabelStyle}>Trigger</label>
            <select
              value={triggerFingerprint}
              onChange={(e) => {
                setTriggerFingerprint(e.target.value);
                setPage(1);
              }}
              style={{
                width: "100%",
                padding: "10px 12px",
                borderRadius: "12px",
                border: "1px solid rgba(17,17,17,0.14)",
                fontSize: "14px",
                background: "#fff",
              }}
            >
              <option value="">Alla triggers med IC-bolag</option>
              {triggerSelectOptions.map((o) => (
                <option key={o.fingerprint} value={o.fingerprint}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              fontSize: "14px",
              color: "#3f3a34",
              cursor: "pointer",
              paddingBottom: "10px",
              whiteSpace: "nowrap",
            }}
          >
            <input
              type="checkbox"
              checked={showManuallyExcluded}
              onChange={(e) => {
                setShowManuallyExcluded(e.target.checked);
                setPage(1);
              }}
              style={{ width: "16px", height: "16px", cursor: "pointer" }}
            />
            Visa manuellt exkluderade bolag
          </label>
          <button
            type="button"
            onClick={applyFilters}
            style={{
              padding: "10px 18px",
              borderRadius: "999px",
              border: "1px solid #111",
              background: "#111",
              color: "#fff",
              cursor: "pointer",
              fontSize: "14px",
            }}
          >
            Uppdatera lista
          </button>
        </div>

        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "12px",
            alignItems: "end",
            marginBottom: "18px",
            fontFamily: 'Arial, Helvetica, sans-serif',
          }}
        >
          <div style={{ flex: "0 1 110px", minWidth: "90px" }}>
            <label style={filterLabelStyle}>Omsättning min</label>
            <input
              type="text"
              inputMode="decimal"
              value={netSalesMinInput}
              onChange={(e) => setNetSalesMinInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && applyFilters()}
              placeholder="MSEK"
              style={inputNumStyle}
            />
          </div>
          <div style={{ flex: "0 1 110px", minWidth: "90px" }}>
            <label style={filterLabelStyle}>Omsättning max</label>
            <input
              type="text"
              inputMode="decimal"
              value={netSalesMaxInput}
              onChange={(e) => setNetSalesMaxInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && applyFilters()}
              placeholder="MSEK"
              style={inputNumStyle}
            />
          </div>
          <div style={{ flex: "0 1 110px", minWidth: "90px" }}>
            <label style={filterLabelStyle}>EBITDA min</label>
            <input
              type="text"
              inputMode="decimal"
              value={ebitdaMinInput}
              onChange={(e) => setEbitdaMinInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && applyFilters()}
              placeholder="MSEK"
              style={inputNumStyle}
            />
          </div>
          <div style={{ flex: "0 1 110px", minWidth: "90px" }}>
            <label style={filterLabelStyle}>EBITDA max</label>
            <input
              type="text"
              inputMode="decimal"
              value={ebitdaMaxInput}
              onChange={(e) => setEbitdaMaxInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && applyFilters()}
              placeholder="MSEK"
              style={inputNumStyle}
            />
          </div>
          <div
            style={{
              fontSize: "12px",
              color: "#7a746c",
              maxWidth: "320px",
              lineHeight: 1.45,
              paddingBottom: "4px",
            }}
          >
            Omsättning och EBITDA enligt platform_universe (MSEK). Trigger-valet filtrerar på den
            IC-rad som är senast uppdaterad per bolag (samma trigger som visas under bolagsnamnet).
          </div>
        </div>

        <div style={{ fontSize: "12px", color: "#7a746c", marginBottom: "10px", fontFamily: 'Arial, Helvetica, sans-serif' }}>
          Klicka på kolumnrubrik för att sortera. Klicka igen för att växla stigande/fallande.
        </div>

        {error && (
          <p style={{ color: "#b42318", fontFamily: 'Arial, Helvetica, sans-serif' }}>{error}</p>
        )}

        <div
          style={{
            overflowX: "auto",
            border: "1px solid rgba(17,17,17,0.10)",
            borderRadius: "14px",
            background: "#fff",
          }}
        >
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              fontSize: "14px",
              fontFamily: 'Arial, Helvetica, sans-serif',
            }}
          >
            <thead>
              <tr style={{ borderBottom: "1px solid rgba(17,17,17,0.12)" }}>
                {SORT_KEYS.map(({ id, label }) => (
                  <th
                    key={id}
                    onClick={() => onHeaderClick(id)}
                    style={{
                      textAlign: "left",
                      padding: "12px 14px",
                      fontWeight: 500,
                      cursor: "pointer",
                      whiteSpace: "nowrap",
                      color: sortBy === id ? "#111" : "#5f5a52",
                      userSelect: "none",
                    }}
                  >
                    {label}
                    {sortBy === id && (
                      <span style={{ marginLeft: "6px", fontSize: "12px" }}>
                        {sortDir === "desc" ? "↓" : "↑"}
                      </span>
                    )}
                  </th>
                ))}
                <th style={{ textAlign: "left", padding: "12px 14px", color: "#5f5a52", fontWeight: 500 }}>
                  Bransch
                </th>
              </tr>
            </thead>
            <tbody>
              {loading && rows.length === 0 ? (
                <tr>
                  <td colSpan={SORT_KEYS.length + 1} style={{ padding: "24px", color: "#5f5a52" }}>
                    Laddar…
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={SORT_KEYS.length + 1} style={{ padding: "24px", color: "#5f5a52" }}>
                    Inga träffar.
                  </td>
                </tr>
              ) : (
                rows.map((r) => (
                  <tr
                    key={r.company_id}
                    onClick={() => setSelectedCompanyId(r.company_id)}
                    style={{
                      borderBottom: "1px solid rgba(17,17,17,0.06)",
                      cursor: "pointer",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = "#faf8f4";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = "transparent";
                    }}
                  >
                    <td style={{ padding: "12px 14px", verticalAlign: "top", whiteSpace: "nowrap" }}>
                      {formatDate(r.last_ic_at)}
                    </td>
                    <td style={{ padding: "12px 14px", verticalAlign: "top" }}>
                      <div style={{ fontWeight: 500 }}>{r.company_name || "–"}</div>
                      <div style={{ fontSize: "12px", color: "#7a746c", marginTop: "4px" }}>
                        {r.company_id}
                      </div>
                      {r.trigger_title && (
                        <div
                          style={{
                            fontSize: "11px",
                            color: "#9a9288",
                            marginTop: "6px",
                            lineHeight: 1.35,
                            maxWidth: "280px",
                          }}
                        >
                          Trigger: {r.trigger_title}
                        </div>
                      )}
                    </td>
                    <td style={{ padding: "12px 14px", verticalAlign: "top" }}>
                      {formatSek(r.net_sales)}
                    </td>
                    <td style={{ padding: "12px 14px", verticalAlign: "top" }}>
                      {formatSek(r.ebitda)}
                    </td>
                    <td style={{ padding: "12px 14px", verticalAlign: "top", whiteSpace: "nowrap" }}>
                      {formatIcConfidence(r.ic_confidence)}
                    </td>
                    <td style={{ padding: "12px 14px", verticalAlign: "top" }}>
                      <span
                        style={{
                          color:
                            icDecisionColor[r.latest_ic_decision] || "#333",
                          fontWeight: 500,
                        }}
                      >
                        {prettifyStatusLabel(r.latest_ic_decision || "–")}
                      </span>
                      <div style={{ fontSize: "12px", color: "#7a746c", marginTop: "4px" }}>
                        {prettifyStatusLabel(r.latest_status || "–")}
                      </div>
                    </td>
                    <td style={{ padding: "12px 14px", verticalAlign: "top", maxWidth: "220px" }}>
                      <span style={{ fontSize: "13px", lineHeight: 1.4, display: "block" }}>
                        {r.industry || "–"}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginTop: "16px",
              fontFamily: 'Arial, Helvetica, sans-serif',
              fontSize: "14px",
              color: "#5f5a52",
            }}
          >
            <span>
              Sida {page} av {totalPages}
            </span>
            <div style={{ display: "flex", gap: "10px" }}>
              <button
                type="button"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                style={{
                  padding: "8px 14px",
                  borderRadius: "999px",
                  border: "1px solid rgba(17,17,17,0.2)",
                  background: "#fff",
                  cursor: page <= 1 ? "not-allowed" : "pointer",
                  opacity: page <= 1 ? 0.5 : 1,
                }}
              >
                Föregående
              </button>
              <button
                type="button"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                style={{
                  padding: "8px 14px",
                  borderRadius: "999px",
                  border: "1px solid rgba(17,17,17,0.2)",
                  background: "#fff",
                  cursor: page >= totalPages ? "not-allowed" : "pointer",
                  opacity: page >= totalPages ? 0.5 : 1,
                }}
              >
                Nästa
              </button>
            </div>
          </div>
        )}
      </Section>

      {selectedCompanyId && (
        <CompanyProfile
          companyId={selectedCompanyId}
          onClose={() => setSelectedCompanyId(null)}
        />
      )}
    </>
  );
}

export default CompanyIcDashboard;
